import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { MenuOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { convertToVwMob } from '../../Utils/responsive';
import mainLogo from '../../assets/Logo/main_logo.png';
import userIconImg from '../../assets/Icon/sidbar/mob/user_icon.png';
import { PATHS } from '../../routes/paths';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectSession } from '../../Module/Auth/Presentation/authSelectors';
import { selectUserProfile } from '../../Module/User/Presentation/userSelectors';
import { signOutAndClear } from '../../Module/Auth/Presentation/authSlice';
import { isIndividualUserType } from '../../Utils/userType';
import { formatUserFullName } from '../../Utils/formatUserName';


type Props = {
  title?: string;
  left?: React.ReactNode;
  right?: React.ReactNode;
};

// Mobile design: logo + hamburger only (no center title), per screenshot.
const MobileNavBar: React.FC<Props> = ({ left, right }) => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const session = useAppSelector(selectSession);
  const userProfile = useAppSelector(selectUserProfile);
  const isAccountPage =
    location.pathname === PATHS.MY_ACCOUNT ||
    location.pathname.startsWith(`${PATHS.MY_ACCOUNT}/`);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);

  const username = useMemo(() => {
    if (userProfile) {
      const organization = typeof userProfile.organization === 'string' ? userProfile.organization.trim() : '';
      const first = typeof userProfile.first_name === 'string' ? userProfile.first_name : '';
      const last = typeof userProfile.last_name === 'string' ? userProfile.last_name : '';
      const email = typeof userProfile.email === 'string' ? userProfile.email : '';
      const fullName = (
        formatUserFullName(first, last) || email
      ).trim();

      if (!isIndividualUserType(userProfile.user_type)) {
        return organization || fullName || 'username';
      }

      return fullName || organization || 'username';
    }

    const u = (session as { user?: Record<string, unknown> } | undefined)?.user ?? {};
    const name = u['name'];
    const first = u['first_name'];
    const last = u['last_name'];
    const email = u['email'];

    if (typeof name === 'string' && name.trim()) return name;
    const full = formatUserFullName(first as string, last as string);
    if (full) return full;
    if (typeof email === 'string' && email.trim()) return email;
    return 'username';
  }, [session, userProfile]);

  const profileImage = useMemo(() => {
    const primary = userProfile?.image_url || userProfile?.sns_thumbnail;
    if (typeof primary === 'string' && primary.trim()) return primary;
    const u = (session as { user?: Record<string, unknown> } | undefined)?.user ?? {};
    const fallback = u['image_url'] || u['sns_thumbnail'] || u['profile_image'] || u['avatar'];
    return typeof fallback === 'string' && fallback.trim() ? fallback : '';
  }, [session, userProfile]);

  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  useEffect(() => {
    if (!isDrawerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!isDrawerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeDrawer, isDrawerOpen]);

  useEffect(() => {
    if (!profileImage) return;
    setProfileImageError(false);
  }, [profileImage]);

  const handleMyAccount = useCallback(() => {
    closeDrawer();
    navigate(isAccountPage ? PATHS.PROJECT_MANAGEMENT : PATHS.MY_ACCOUNT);
  }, [closeDrawer, isAccountPage, navigate]);

  const handleProjectManagement = useCallback(() => {
    closeDrawer();
    navigate(PATHS.PROJECT_MANAGEMENT);
  }, [closeDrawer, navigate]);

  const handleSupport = useCallback(() => {
    closeDrawer();
    window.open('http://pf.kakao.com/_xkHknxd/chat', '_blank', 'noopener,noreferrer');
  }, [closeDrawer]);

  const handleLogout = useCallback(() => {
    closeDrawer();
    dispatch(signOutAndClear());
    navigate(PATHS.LOGIN, { replace: true });
  }, [closeDrawer, dispatch, navigate]);

  return (
    <>
      <Bar>
        <Left>{left ?? (
          <LogoWrap>
            <Logo src={mainLogo} alt="Mission Fund" />
          </LogoWrap>
        )}</Left>
        <Right>
          {right ?? (
            <MenuButton
              type="button"
              aria-label="menu"
              aria-expanded={isDrawerOpen}
              aria-controls="mobile-nav-drawer"
              onClick={() => setIsDrawerOpen((prev) => !prev)}
            >
              <MenuOutlined />
            </MenuButton>
          )}
        </Right>
      </Bar>

      <DrawerOverlay $open={isDrawerOpen} onClick={closeDrawer} />
      <DrawerPanel id="mobile-nav-drawer" $open={isDrawerOpen} role="dialog" aria-modal="true">
        <UserSection>
          {profileImage && !profileImageError ? (
            <UserIcon
              src={profileImage}
              alt={username}
              onError={() => setProfileImageError(true)}
            />
          ) : (
            <UserIcon src={userIconImg} alt="" style={{ objectFit: 'contain' }} />
          )}
          <UserName title={username}>{username}</UserName>
        </UserSection>

        <MenuList>
          <MenuItem type="button" onClick={handleProjectManagement}>
            {t('translation:common.layout.projectManagement')}
          </MenuItem>
          <MenuItem type="button" onClick={handleMyAccount}>
            {t('translation:nav.myAccount')}
          </MenuItem>
          <MenuItem type="button" onClick={handleSupport}>
            {t('translation:nav.support')}
          </MenuItem>
          <MenuItem type="button" onClick={handleLogout}>
            {t('translation:nav.logout')}
          </MenuItem>
        </MenuList>
      </DrawerPanel>
    </>
  );
};

const Bar = styled.header`
  height: ${convertToVwMob(70)};
  background: ${({ theme }) => theme.colors.other.mainBackground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${convertToVwMob(16)};
  position: relative;
  flex: 0 0 auto;
  z-index: 1200;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
`;

const LogoWrap = styled.div`
  display: inline-flex;
  align-items: center;
`;

const Logo = styled.img`
  height: ${convertToVwMob(35)};
  width: ${convertToVwMob(136)};
  object-fit: contain;
`;

const MenuButton = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.colors.grayScale[80]};
  border-radius: ${convertToVwMob(10)};
  width: ${convertToVwMob(36)};
  height: ${convertToVwMob(36)};
  font-size: ${convertToVwMob(18)};
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:focus {
    outline: none;
  }
  
  &:active {
    border: none;
    outline: none;
  }
`;

const DrawerOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  top: ${convertToVwMob(70)};
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 180ms ease-out;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  z-index: 1100;
`;

const DrawerPanel = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: ${convertToVwMob(70)};
  right: 0;
  height: calc(100vh - ${convertToVwMob(70)});
  width: ${convertToVwMob(220)};
  background: #ffffff;
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 180ms ease-out;
  z-index: 1101;
  display: flex;
  flex-direction: column;
  box-shadow: -${convertToVwMob(6)} 0 ${convertToVwMob(18)} rgba(0, 0, 0, 0.12);
`;

const UserSection = styled.div`
  padding: ${convertToVwMob(36)} ${convertToVwMob(16)} ${convertToVwMob(18)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #eee;
`;

const UserIcon = styled.img`
  width: ${convertToVwMob(96)};
  height: ${convertToVwMob(96)};
  border-radius: 50%;
  object-fit: cover;
  background: #f3f3f3;
`;

const UserName = styled.div`
  margin-top: ${convertToVwMob(12)};
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(22)};
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  text-align: center;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MenuItem = styled.button`
  appearance: none;
  width: 100%;
  height: ${convertToVwMob(56)};
  border: none;
  border-top: 1px solid #eee;
  background: #ffffff;

  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  text-align: center;

  cursor: pointer;

  &:active {
    background: ${({ theme }) => theme.colors.grayScale[5]};
  }

  &:first-child {
    border-top: none;
  }
`;

export default MobileNavBar;


